import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { BillingProvider } from "@/context/BillingContext";
import { LangProvider } from "@/context/LangContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Flow from "@/pages/Flow";
import InstallBanner from "@/components/InstallApp";
import TypingGuide from "@/components/TypingGuide";
import { SHARED_KEY } from "@/components/SharedText";

const Billing = lazy(() => import("@/pages/Billing"));
const Invite = lazy(() => import("@/pages/Invite"));
const Admin = lazy(() => import("@/pages/Admin"));
const Listen = lazy(() => import("@/pages/Listen"));
const KeyboardConnect = lazy(() => import("@/pages/KeyboardConnect"));
const KeyboardApp = lazy(() => import("@/pages/KeyboardApp"));
const IPhoneApp = lazy(() => import("@/pages/IPhoneApp"));
const History = lazy(() => import("@/pages/History"));
const Dictionary = lazy(() => import("@/pages/Dictionary"));
const Snippets = lazy(() => import("@/pages/Snippets"));
const Stats = lazy(() => import("@/pages/Stats"));
const Settings = lazy(() => import("@/pages/Settings"));
const Contact = lazy(() => import("@/pages/Contact"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const Loading = () => <div className="flow-bg min-h-screen" data-testid="page-loading" />;

const P = ({ children }) => <ProtectedRoute>{children}</ProtectedRoute>;

function AppRouter() {
  const location = useLocation();
  const ref = new URLSearchParams(location.search).get("ref");
  if (ref) localStorage.setItem("mulankan_ref", ref.trim().toUpperCase());
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  if (location.pathname === "/share") {
    const q = new URLSearchParams(location.search);
    const body = [q.get("title"), q.get("text"), q.get("url")].filter(Boolean)
      .filter((v, i, a) => a.indexOf(v) === i).join("\n\n").trim();
    if (body) sessionStorage.setItem(SHARED_KEY, body);
    return <Navigate to="/flow" replace />;
  }
  return (
    <Suspense fallback={<Loading />}>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/flow" element={<P><Flow /></P>} />
      <Route path="/listen" element={<P><Listen /></P>} />
      <Route path="/keyboard" element={<KeyboardApp />} />
      <Route path="/download" element={<KeyboardApp />} />
      <Route path="/iphone" element={<IPhoneApp />} />
      <Route path="/keyboard-connect" element={<P><KeyboardConnect /></P>} />
      <Route path="/history" element={<P><History /></P>} />
      <Route path="/dictionary" element={<P><Dictionary /></P>} />
      <Route path="/snippets" element={<P><Snippets /></P>} />
      <Route path="/stats" element={<P><Stats /></P>} />
      <Route path="/settings" element={<P><Settings /></P>} />
      <Route path="/billing" element={<P><Billing /></P>} />
      <Route path="/invite" element={<P><Invite /></P>} />
      <Route path="/admin" element={<P><Admin /></P>} />
      <Route path="/dashboard" element={<Navigate to="/flow" replace />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/about" element={<LegalPage slug="about" />} />
      <Route path="/privacy" element={<LegalPage slug="privacy" />} />
      <Route path="/terms" element={<LegalPage slug="terms" />} />
      <Route path="/refund" element={<LegalPage slug="refund" />} />
      <Route path="/return" element={<LegalPage slug="return" />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <LangProvider>
        <BrowserRouter>
          <AuthProvider>
            <BillingProvider>
              <AppRouter />
            </BillingProvider>
            <InstallBanner />
            <TypingGuide />
            <Toaster position="bottom-center" theme="dark" richColors />
          </AuthProvider>
        </BrowserRouter>
      </LangProvider>
    </div>
  );
}

export default App;
